import React, { useEffect, useRef, useState } from "react";
import {
  DataGrid,
  GridColDef,

  GridOverlay,
  GridToolbarContainer,
  // GridToolbarExport,
  GridToolbarDensitySelector,
  // GridToolbarFilterButton,

} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Stack
} from "@mui/material";
import {

  Print,
  CameraAlt,
  Refresh,
  FilterAlt,
  DateRange,
  PictureAsPdf,
  TableChart
} from "@mui/icons-material";
import { AxiosResponse } from "axios";
import html2canvas from "html2canvas";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import http from "../../../commonFunctions/httpClient";
import { ApiResponse, formatCurrency } from "../../../commonFunctions/CommonFunctions";

export type ReportColumnType =
  | "text"
  | "date"
  | "currency"
  | "number"
  | "percentage";

export type ReportGridColDef = GridColDef & {
  reportType?: "currency" | "percentage";
};

interface ReportTableProps {
  title: string;
  subtitle?: string;
  endpoint: string;
  columns: ReportGridColDef[];
  filters: Record<string, any>;
  onRefresh?: () => void;
  showExportButtons?: boolean;
  maxHeight?: number | string;
  nestedColumnValue?: string;
}

const ReportTable: React.FC<ReportTableProps> = ({
  title,
  subtitle,
  endpoint,
  columns,
  filters,
  onRefresh,
  showExportButtons = true,
  maxHeight = 520,
  nestedColumnValue = "items"
}) => {
  const theme = useTheme();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const defaultPagination = {
    totalCount: 0,
    page: 1,
    pageSize: 10
  };

  useEffect(() => {
    fetchReport();
  }, [filters, paginationModel]);

  const flattenData = (data: any[], nestedKey: string) => {
    const rows: any[] = [];

    data.forEach((parent) => {
      const nestedItems = parent[nestedKey];

      if (Array.isArray(nestedItems) && nestedItems.length > 0) {
        nestedItems.forEach((item: any) => {
          rows.push({
            ...parent,
            ...item
          });
        });
      } else {
        rows.push({
          ...parent
        });
      }
    });

    return rows;
  };

  const fetchReport = async () => {
    setLoading(true);

    console.log("Fetching report with filters:", filters, "and pagination:", paginationModel);
    try {
      const response: ApiResponse = await http.get(endpoint, {
        params: {
          ...filters,
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize
        }
      });

      console.log('Report API response:', response);
      // const result = response?.data || {};

      const data = response?.data || [];
      const pagination = response?.pagination ?? defaultPagination;

      const processedRows = flattenData(data, nestedColumnValue);

      setRows(processedRows);
      setTotalRows(pagination.totalCount || 0);
      setLastUpdated(new Date());

    } catch (err) {
      console.error("Failed to load report:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCellValue = (col: ReportGridColDef, value: any, row?: any) => {

    if (value === null || value === undefined) return "";

    switch (col.type) {
      case "date":
        return new Date(value).toLocaleDateString("en-GB");

      case "number":
        return Number(value).toLocaleString("en-IN");
    }

    switch (col.reportType) {
      case "currency":
        return formatCurrency(value);

      case "percentage":
        return `${Number(value).toFixed(2)}%`;

      default:
        return value;
    }
  };

  const handleRefresh = () => {
    fetchReport();
    if (onRefresh) onRefresh();
  };

  const captureRef = useRef<HTMLDivElement>(null);

  const takeScreenshot = async () => {
    if (captureRef.current) {
      try {
        const canvas = await html2canvas(captureRef.current, {
          scale: 1.5,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true,
          useCORS: true
        });

        const imageUrl = canvas.toDataURL('image/png');
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `${title.toLowerCase().replace(/\s+/g, '_')}_screenshot.png`;
        link.click();
      } catch (error) {
        console.error("Screenshot failed:", error);
      }
    }
  };

  const exportToExcel = () => {

    const exportData = rows.map((row: any) => {
      const formattedRow: any = {};

      columns.forEach(col => {
        const value = row[col.field];
        formattedRow[col.headerName || col.field] =
          formatCellValue(col as ReportGridColDef, value, row);
      });

      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    XLSX.writeFile(
      workbook,
      `${title.toLowerCase().replace(/\s+/g, "_")}_export.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text(title, 14, 15);

    // Add subtitle if exists
    if (subtitle) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(subtitle, 14, 22);
    }

    // Add date
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, subtitle ? 28 : 22);

    // Prepare table data
    const tableColumns = columns.map(col => ({
      header: col.headerName || col.field,
      dataKey: col.field
    }));

    const tableRows = rows.map((row: any) =>
      columns.map(col =>
        formatCellValue(col as ReportGridColDef, row[col.field], row)
      )
    );

    autoTable(doc, {
      head: [tableColumns.map(col => col.header)],
      body: tableRows,
      startY: subtitle ? 32 : 26,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [76, 175, 80], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 30 }
    });

    doc.save(`${title.toLowerCase().replace(/\s+/g, '_')}_export.pdf`);
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const tableContent = `
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #333; font-size: 24px; }
              .subtitle { color: #666; font-size: 14px; }
              .date { color: #999; font-size: 12px; margin-bottom: 20px; }
              table { width: 100%; border-collapse: collapse; }
              th { background-color: #4CAF50; color: white; padding: 12px; text-align: left; }
              td { padding: 10px; border-bottom: 1px solid #ddd; }
              tr:nth-child(even) { background-color: #f5f5f5; }
              .footer { margin-top: 20px; color: #999; font-size: 10px; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}
            <div class="date">Generated: ${new Date().toLocaleString()}</div>
            <table>
              <thead>
                <tr>
                  ${columns.map(col => `<th>${col.headerName || col.field}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${rows.map((row: any) => `
                  <tr>
                    ${columns.map(col => {
        const value = row[col.field];
        return `<td>${formatCellValue(col as ReportGridColDef, value, row)}</td>`;
      }).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="footer">Total Records: ${totalRows}</div>
          </body>
        </html>
      `;

      printWindow.document.write(tableContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Custom toolbar component
  const CustomToolbar = () => (
    <GridToolbarContainer sx={{
      p: 1,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.02)
    }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
        {/* <GridToolbarFilterButton /> */}
        <GridToolbarDensitySelector />
        {/* <GridToolbarExport /> */}
        {showExportButtons && (
          <>
            <Tooltip title="Export to Excel">
              <Button
                size="small"
                startIcon={<TableChart />}
                onClick={exportToExcel}
                variant="text"
                sx={{ color: theme.palette.success.main }}
              >
                Excel
              </Button>
            </Tooltip>
            <Tooltip title="Export to PDF">
              <Button
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={exportToPDF}
                variant="text"
                sx={{ color: theme.palette.error.main }}
              >
                PDF
              </Button>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Chip
          icon={<DateRange />}
          label={lastUpdated ? `Updated: ${lastUpdated.toLocaleTimeString()}` : 'Not updated'}
          size="small"
          variant="outlined"
          sx={{ borderRadius: 1 }}
        />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={handleRefresh} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
    </GridToolbarContainer>
  );

  // Custom loading indicator
  const CustomLoadingOverlay = () => (
    <GridOverlay>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2
      }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading report data...
        </Typography>
      </Box>
    </GridOverlay>
  );

  // Custom no rows overlay
  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        p: 3
      }}>
        <FilterAlt sx={{ fontSize: 40, color: alpha(theme.palette.text.disabled, 0.5) }} />
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Try adjusting your filters or refresh the data
        </Typography>
      </Box>
    </GridOverlay>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 4,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2.5,
        px: 1
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          {/* <Tooltip title="Take Screenshot">
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAlt />}
              onClick={takeScreenshot}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }
              }}
            >
              Screenshot
            </Button>
          </Tooltip> */}

          <Tooltip title="Print">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Print />}
              onClick={printReport}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }
              }}
            >
              Print
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        ref={captureRef}
        sx={{
          height: maxHeight,
          width: "100%",
          borderRadius: 1.5,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '& .MuiDataGrid-root': {
            border: 'none'
          }
        }}
      >
        <DataGrid
          rows={rows.map((row: any, index) => ({ id: index + 1, ...row }))}
          columns={columns}
          loading={loading}

          paginationMode="server"

          rowCount={totalRows}

          paginationModel={paginationModel}

          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}

          pageSizeOptions={[10, 25, 50, 100]}

          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: CustomLoadingOverlay,
            noRowsOverlay: CustomNoRowsOverlay
          }}

          disableRowSelectionOnClick
          // getRowHeight={() => "auto"}
          rowHeight={52}

          sx={{
            borderRadius: 1.5,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderBottom: `2px solid ${theme.palette.primary.main}`
            }
          }}
        />
      </Box>

      {totalRows > 0 && (
        <Box sx={{
          mt: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          px: 1
        }}>
          <Chip
            label={`Total Records: ${totalRows}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 1 }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default ReportTable;